import {
  JSONRPCClient,
  JSONRPCRequest,
  JSONRPCServer,
  JSONRPCServerAndClient,
} from 'json-rpc-2.0';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';
import { requestRateMonitor, memoryDiagnostics } from '@ais-ui/utils';
import type { RequestCategory } from '@ais-ui/utils';
import { saveCredentials } from './tokenStorage';
import {
  isAuthFailure,
  isNewLogonError,
  isSessionTimeoutError,
  isConnectivityIssue,
  isExpectedSessionSignal,
  isTransportUnavailableSignal,
  categorizeErrorMessage,
  buildSessionErrorDetail,
  RETIRED_SESSION_REJECTION_PREFIX,
  SessionUnavailableError,
} from './sessionErrorUtils';
import type { SessionErrorDetail } from './sessionErrorUtils';

type LoginResponse = {
  accountDisabled: boolean;
  accountLocked: boolean;
  alreadyLoggedIn: boolean;
  changePassword: boolean;
  email: string;
  groups: string;
  logo: string;
  reason: string;
  sessionUuid: string;
  success: boolean;
  userName: string;
  userUuid: string;
};

export interface Session {
  request: (method: string, params: any) => PromiseLike<any>;
  serverAndClient: JSONRPCServerAndClient | undefined;
  webSocket: WebSocket | undefined;
  socketId: string;
  /** The sessionId that was baked into this session at creation time. Empty string means the session has no valid auth token. */
  sessionId: string;
  type: 'olSession' | 'dbSession' | 'replaySession';
  /** Returns only stable fields so JSON.stringify (e.g. React Query key hashing) never changes due to mutable internal state. */
  toJSON: () => { socketId: string; type: string };
}

type SessionType = 'olSession' | 'dbSession' | 'replaySession';

/** RFC 6455 "normal closure" - the socket did its job and is being retired deliberately. */
const NORMAL_CLOSURE_CODE = 1000;

const SESSION_TO_CATEGORY: Record<SessionType, RequestCategory> = {
  olSession: 'olserver',
  dbSession: 'dbserver',
  replaySession: 'replayserver',
};

function dispatchCustomEvent(eventName: string, detail: any = null): void {
  const event = new CustomEvent(eventName, { detail: detail });
  window.dispatchEvent(event);
}

/**
 * `connectionClosed` event detail. `webSocket` is what lets a listener tell
 * the live session's socket apart from a leftover one of the same type (an
 * earlier login's, a failed reconnect attempt's) - the type alone matches
 * both.
 */
export interface ConnectionClosedDetail {
  sessionType: SessionType;
  webSocket: WebSocket;
}

/**
 * `sessionError` event detail: the classification, plus the login it is
 * about. The backend refuses a sessionId, not a socket, and a reconnect still
 * priming its socket for an earlier login can receive the backend's refusal
 * of that login after a new one has taken over. `sessionId` is undefined for
 * a socket no session has been created for yet (login itself).
 */
export interface SessionErrorEventDetail extends SessionErrorDetail {
  sessionId: string | undefined;
}

// A socket's message handler is attached when the socket opens, before the
// login it will serve is known; `createSession` records that login here.
const sessionIdBySocket = new WeakMap<WebSocket, string>();

function dispatchWebSocketCloseEvent(websocket: WebSocket) {
  const { url } = websocket;
  if (
    !url.includes('olserver') &&
    !url.includes('dbserver') &&
    !url.includes('replay')
  ) {
    dispatchCustomEvent('connectionClosed');
    return;
  }

  const detail: ConnectionClosedDetail = {
    sessionType: getSessionTypeFromUrl(url),
    webSocket: websocket,
  };
  dispatchCustomEvent('connectionClosed', detail);
}

function getSessionTypeFromUrl(url: string): SessionType {
  if (url.includes('olserver')) {
    return 'olSession';
  } else if (url.includes('dbserver')) {
    return 'dbSession';
  } else if (url.includes('replay')) {
    return 'replaySession';
  }
  return 'olSession'; // default
}

// When each socket last delivered a frame - see `getSessionLastActivity`.
const lastFrameAtBySocket = new WeakMap<WebSocket, number>();

/**
 * When the session's socket last delivered a frame, whatever it carried - a
 * reply, an error, a server push - as `performance.now()`, so a clock change
 * can't skew it. Undefined if it never has. A frame proves the socket is alive
 * even when one particular request is slow to be answered.
 */
export function getSessionLastActivity(
  session: Session | undefined,
): number | undefined {
  const socket = session?.webSocket;
  return socket ? lastFrameAtBySocket.get(socket) : undefined;
}

// When each socket last answered a request successfully (see isSuccessfulReply).
// Wall-clock, unlike the frame timestamp above, because it is compared with
// the grace period's start, which must also count time a frozen tab was away.
const lastOkAtBySocket = new WeakMap<WebSocket, number>();

/**
 * True when the session's socket is open and has answered a request successfully
 * since `sinceMs` (`Date.now()` terms). Every RPC carries the sessionUuid
 * and the backend answers an invalid one with an error, so a successful reply -
 * a poll's result - proves the session is valid *and* being served. An error
 * frame, a server push, or a `false` result proves nothing of the sort, so
 * none of them count. A socket that is not open never does, however fresh its last reply.
 */
export function hasSessionAnsweredSince(
  session: Session | undefined,
  sinceMs: number,
): boolean {
  const socket = session?.webSocket;
  const lastOkAt = socket ? lastOkAtBySocket.get(socket) : undefined;
  return (
    socket?.readyState === WebSocket.OPEN &&
    lastOkAt !== undefined &&
    lastOkAt > sinceMs
  );
}

/** How recently a socket must have answered successfully to count as delivering. */
export const SESSION_DELIVERY_WINDOW_MS = 15_000;

/**
 * True while the session's socket is open and answered successfully within
 * {@link SESSION_DELIVERY_WINDOW_MS}: it is serving the map, whatever any one
 * request (a session-validity check, say) is doing.
 */
export function isSessionDelivering(
  session: Session | undefined,
  windowMs: number = SESSION_DELIVERY_WINDOW_MS,
): boolean {
  return hasSessionAnsweredSince(session, Date.now() - windowMs);
}

/**
 * A reply that proves the backend accepted the request's session: it answers a
 * request (server pushes carry no `id`), carries no error, and isn't a `false`
 * result - which is how a session-validity check says the session is invalid.
 */
function isSuccessfulReply(messageData: {
  id?: unknown;
  error?: unknown;
  result?: unknown;
}): boolean {
  return (
    messageData.id !== undefined &&
    !messageData.error &&
    messageData.result !== false
  );
}

function initializeWebSocketEventHandlers(
  serverwebSocket: WebSocket,
  serverAndClient: {
    receiveAndSend: (messageData: any) => void;
    rejectAllPendingRequests: (message: string) => void;
  },
): void {
  serverwebSocket.onmessage = (event: MessageEvent) => {
    lastFrameAtBySocket.set(serverwebSocket, performance.now());
    try {
      const messageData = JSON.parse(event.data.toString());
      if (isSuccessfulReply(messageData)) {
        lastOkAtBySocket.set(serverwebSocket, Date.now());
      }

      // Check for session errors BEFORE passing through for immediate detection
      if (messageData.error) {
        const errorMessage = messageData.error.message || '';

        // Determine if this is an auth failure or connectivity issue
        const isAuthError =
          isAuthFailure(errorMessage) ||
          isNewLogonError(errorMessage) ||
          isSessionTimeoutError(errorMessage);

        if (isAuthError) {
          console.warn('Session error detected:', errorMessage);

          const sessionType = getSessionTypeFromUrl(serverwebSocket.url);
          const errorCategory = categorizeErrorMessage(errorMessage);

          // Expected session signals (new-logon / expired / not-found session)
          // are handled by the re-auth flow below. They are NOT captured as
          // Sentry errors — doing so floods the project with non-actionable
          // noise (issue FMM). A breadcrumb keeps the context on any real
          // error that follows without creating an issue of its own.
          Sentry.addBreadcrumb({
            category: 'session',
            message: `Session re-auth signal: ${errorMessage}`,
            level: 'info',
            data: {
              sessionType,
              error_category: errorCategory,
              url: serverwebSocket.url,
              error: messageData.error,
            },
          });

          // Dispatch a single session error event with all classification flags
          const detail: SessionErrorEventDetail = {
            ...buildSessionErrorDetail(errorMessage, sessionType),
            sessionId: sessionIdBySocket.get(serverwebSocket) || undefined,
          };
          dispatchCustomEvent('sessionError', detail);

          // Still pass through to handle the response properly
          serverAndClient.receiveAndSend(messageData);
          return;
        }

        const sessionType = getSessionTypeFromUrl(serverwebSocket.url);
        const errorCategory = categorizeErrorMessage(
          messageData.error.message || '',
        );

        // Connectivity issues are logged as warnings (less alarming)
        if (isConnectivityIssue(errorMessage)) {
          Sentry.captureMessage(
            `WebSocket connectivity issue: ${messageData.error.message || 'Unknown error'}`,
            {
              level: 'warning',
              tags: {
                error_type: 'websocket_connectivity_issue',
                sessionType,
                error_category: errorCategory,
                error_severity: 'connectivity',
              },
              extra: {
                error: messageData.error,
                url: serverwebSocket.url,
                errorCode: messageData.error.code,
              },
            },
          );
        } else {
          // Other errors - capture as warning by default
          Sentry.captureMessage(
            `WebSocket error: ${messageData.error.message || 'Unknown error'}`,
            {
              level: 'warning',
              tags: {
                error_type: 'websocket_message_error',
                sessionType,
                error_category: errorCategory,
                error_severity: 'unknown',
              },
              extra: {
                error: messageData.error,
                url: serverwebSocket.url,
                errorCode: messageData.error.code,
              },
            },
          );
        }
      }

      // Handle special server-initiated messages
      if (messageData.method === 'update') {
        dispatchCustomEvent('eventNotification', messageData.params);
        return;
      }

      if (messageData.method === 'overlay') {
        dispatchCustomEvent('replayOverlay', messageData.params);
        return;
      }

      // Pass everything else through
      serverAndClient.receiveAndSend(messageData);
    } catch (e) {
      console.error('Failed to parse message data:', e);

      const sessionType = getSessionTypeFromUrl(serverwebSocket.url);

      Sentry.captureException(e, {
        tags: {
          error_type: 'websocket_parse_error',
          sessionType,
        },
        extra: {
          url: serverwebSocket.url,
          rawData: event.data?.toString?.().substring(0, 500),
        },
      });
    }
  };

  serverwebSocket.onclose = (closeEvent: CloseEvent) => {
    // Reject any in-flight RPCs so their awaiting promises settle instead of
    // hanging forever. `serverAndClient.request` has no timeout of its own and
    // a socket that closes mid-request otherwise never resolves nor rejects,
    // which can leave React Query queries stuck in `fetching` (e.g. the map
    // overlay after an olserver drop) and, on the reconnect path, prevent a
    // fresh session from being advertised before the grace period gives up.
    serverAndClient.rejectAllPendingRequests(
      `WebSocket closed before response (code ${closeEvent.code}${
        closeEvent.reason ? `, reason: ${closeEvent.reason}` : ''
      })`,
    );

    // Only log if it's an unexpected close
    if (!closeEvent.wasClean || closeEvent.code !== 1000) {
      console.warn(
        `WebSocket Closed unexpectedly with url ${serverwebSocket.url}:`,
        closeEvent,
      );

      // Unexpected closes are connectivity issues - log as warning
      Sentry.captureMessage(
        `WebSocket closed unexpectedly: ${closeEvent.reason || 'No reason'}`,
        {
          level: 'warning',
          tags: {
            error_type: 'unexpected_close',
            close_code: closeEvent.code.toString(),
            error_severity: 'connectivity',
          },
          extra: {
            url: serverwebSocket.url,
            code: closeEvent.code,
            reason: closeEvent.reason,
            wasClean: closeEvent.wasClean,
          },
        },
      );
    }

    dispatchWebSocketCloseEvent(serverwebSocket);
  };
}

/**
 * Tears down a session's websocket: the one a reconnect has just replaced, or
 * every session's on logout.
 *
 * A reconnect mints a brand-new socket, but the one it supersedes used to be
 * left untouched (and logout left all three open). That orphan stayed a GC
 * root (an open socket with live handlers is never collected) and, while the
 * server kept pushing at it, went on re-dispatching the same global events as
 * the live session - so every listener downstream did its work once per
 * orphan. `useGracefulLogout` reconnects while the previous socket is still
 * OPEN, which is exactly the case that accumulates them.
 *
 * The handlers MUST come off before the close. `onclose` dispatches
 * `connectionClosed`, which SessionProvider reads as "this session is gone":
 * it would clear the session we just reconnected and start the cycle again
 * (and for dbSession/replaySession, which reconnect unconditionally, that is
 * an endless loop). Detaching first makes the close silent, so this takes over
 * `onclose`'s one load-bearing side effect - settling in-flight RPCs, which
 * otherwise hang forever because the JSON-RPC client has no timeout of its own.
 *
 * On the close code: this closes a socket whose sessionId is *still in use* by
 * the replacement, so it only holds if the server scopes sessions to the
 * session record rather than to the socket. Three things in this client say it
 * does: `buildRequestParams` sends `sessionUuid` as a parameter on every
 * request (the socket carries no session state); `tryRestoreSessionById`
 * reuses a sessionId on brand-new sockets after a page reload, i.e. after
 * every socket of that session is gone; and the ordinary `connectionClosed`
 * reconnect has always re-used the same sessionId on a fresh socket. There is
 * also no logout RPC, so closing a socket is not how a session is ended.
 * Not proven from this repo: that olserver treats a client-initiated 1000
 * differently from the closes above. If it did, the first reconnect would
 * bounce the tab to login immediately - a loud, first-reconnect failure
 * rather than a silent one, so it cannot reach production unnoticed.
 */
export function retireSession(
  session: Session | undefined,
  reason = 'session was replaced by a reconnect',
): void {
  const socket = session?.webSocket;
  if (!socket) {
    return;
  }
  retiredSockets.add(socket);

  try {
    session?.serverAndClient?.rejectAllPendingRequests(
      `${RETIRED_SESSION_REJECTION_PREFIX}: ${reason}`,
    );
  } catch (error) {
    console.warn('Failed to settle pending requests on retired session', error);
  }

  socket.onclose = null;
  socket.onmessage = null;
  socket.onerror = null;
  socket.onopen = null;

  if (
    socket.readyState === WebSocket.CONNECTING ||
    socket.readyState === WebSocket.OPEN
  ) {
    try {
      socket.close(NORMAL_CLOSURE_CODE, reason);
    } catch (error) {
      console.warn('Failed to close retired websocket', error);
    }
  }
}

// Sockets their owner has deliberately ended - see `isSessionRetired`.
const retiredSockets = new WeakSet<WebSocket>();

/**
 * True once {@link retireSession} has ended this session's socket. Whoever
 * retired it - a logout, a reconnect replacing it, the keep-alive giving up -
 * already owns what happens next, so its requests failing is not news.
 */
export function isSessionRetired(session: Session | undefined): boolean {
  const socket = session?.webSocket;
  return socket !== undefined && retiredSockets.has(socket);
}

/** Retires every session's socket - see {@link retireSession}. */
export function retireSessions(
  sessions: Sessions | undefined,
  reason: string,
): void {
  retireSession(sessions?.olSession, reason);
  retireSession(sessions?.dbSession, reason);
  retireSession(sessions?.replaySession, reason);
}

/**
 * Builds the JSON-RPC client's transport send function for a websocket.
 *
 * Exported for testing. The guard is the important part: browsers silently
 * DISCARD data sent on a CLOSING/CLOSED socket (`WebSocket.send()` only throws
 * while CONNECTING), so a request issued after the socket dropped — e.g. a
 * React Query poll firing during the olserver reconnect grace window — would
 * otherwise resolve the send but never receive a response and hang forever
 * (the JSON-RPC client has no timeout of its own), wedging that query in
 * `fetching`. `onclose`'s `rejectAllPendingRequests` only settles requests that
 * were in flight *at* close; this guard covers requests issued *after* it.
 * Rejecting lets the awaiting promise settle so the query can refetch against
 * the reconnected session on its next tick.
 */
export function createWebSocketSender(
  webSocket: WebSocket,
  basePath: string,
  allowFailure: boolean,
): (jsonRPCRequest: JSONRPCRequest) => PromiseLike<void> {
  return (jsonRPCRequest) => {
    if (webSocket.readyState !== WebSocket.OPEN) {
      // Deliberately do NOT dispatch `sessionConnectionError` here: that
      // surfaces a login-screen error and is meant for connection *setup*
      // failures, whereas a non-OPEN socket mid-session is a transient drop the
      // reconnect/grace-period flow already owns. `request()` recognises this
      // message via `isTransportUnavailableSignal` and keeps it out of Sentry
      // as an error (breadcrumb only).
      return Promise.reject(
        new Error(
          `WebSocket is not open (readyState ${webSocket.readyState}); request "${jsonRPCRequest.method}" not sent`,
        ),
      );
    }

    try {
      webSocket.send(JSON.stringify(jsonRPCRequest));
      return Promise.resolve();
    } catch (error) {
      console.error('Failed basePath:', basePath);
      console.error('Failed to send message:', error);
      if (!allowFailure) {
        const wsUrl = webSocket.url || ``;

        let errorMessage = 'Unknown error';
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        dispatchCustomEvent('sessionConnectionError', {
          websocketUrl: wsUrl,
          error: errorMessage,
        });
      }
      return Promise.reject(error);
    }
  };
}

async function initializeWebSocket(
  basePath: string,
  allowFailure = false,
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  const webSocket = await connectToWebSocket(basePath, allowFailure);

  if (!webSocket) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  const serverAndClient = new JSONRPCServerAndClient(
    new JSONRPCServer(),
    new JSONRPCClient(createWebSocketSender(webSocket, basePath, allowFailure)),
  );

  return { webSocket, serverAndClient };
}

export async function initializeOlWebSocket(
  hostname: string,
  protocol: string,
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  let olServerBasePath: string;
  const websocketProtocol = protocol.includes('https') ? 'wss' : 'ws';
  if (hostname.includes('localhost')) {
    const backendHostname = import.meta.env.VITE_LOCALHOST_BACKEND_HOSTNAME;
    olServerBasePath = `${websocketProtocol}://${backendHostname}/ws/olserver`;
  } else {
    olServerBasePath = `${websocketProtocol}://${hostname}/ws/olserver`;
  }

  const { webSocket, serverAndClient } =
    await initializeWebSocket(olServerBasePath);

  if (!webSocket || !serverAndClient) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  initializeWebSocketEventHandlers(webSocket, serverAndClient);

  return { webSocket, serverAndClient };
}

export async function initializeDbWebSocket(
  hostname: string,
  protocol: string,
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  let dbServerBasePath: string;
  const websocketProtocol = protocol.includes('https') ? 'wss' : 'ws';
  if (hostname.includes('localhost')) {
    const backendHostname = import.meta.env.VITE_LOCALHOST_BACKEND_HOSTNAME;
    dbServerBasePath = `${websocketProtocol}://${backendHostname}/ws/dbserver`;
  } else {
    dbServerBasePath = `${websocketProtocol}://${hostname}/ws/dbserver`;
  }

  const { webSocket, serverAndClient } = await initializeWebSocket(
    dbServerBasePath,
    true,
  );

  if (!webSocket || !serverAndClient) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  initializeWebSocketEventHandlers(webSocket, serverAndClient);

  return { webSocket, serverAndClient };
}

export async function initializeReplayWebSocket(
  hostname: string,
  protocol: string,
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  let replayServerBasePath: string;
  const websocketProtocol = protocol.includes('https') ? 'wss' : 'ws';
  if (hostname.includes('localhost')) {
    const backendHostname = import.meta.env.VITE_LOCALHOST_BACKEND_HOSTNAME;
    replayServerBasePath = `${websocketProtocol}://${backendHostname}/ws/replay`;
  } else {
    replayServerBasePath = `${websocketProtocol}://${hostname}/ws/replay`;
  }

  const { webSocket, serverAndClient } = await initializeWebSocket(
    replayServerBasePath,
    true,
  );

  if (!webSocket || !serverAndClient) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  initializeWebSocketEventHandlers(webSocket, serverAndClient);

  return { webSocket, serverAndClient };
}

export interface Sessions {
  olSession: Session | undefined;
  dbSession: Session | undefined;
  replaySession: Session | undefined;
  sessionId: string | undefined;
}

export interface WebSockets {
  olServerAndClient: JSONRPCServerAndClient | undefined;
  olWebsocket: WebSocket | undefined;
  dbServerAndClient: JSONRPCServerAndClient | undefined;
  dbWebsocket: WebSocket | undefined;
  replayServerAndClient: JSONRPCServerAndClient | undefined;
  replayWebsocket: WebSocket | undefined;
}

export interface ValidatedSessions {
  olSession: Session;
  dbSession: Session | undefined;
  replaySession: Session | undefined;
  sessionId: string;
}

export interface User {
  email: string;
  groups: string[];
  username: string;
  userId: string;
}

export const getEmptySessions = (): Sessions => ({
  olSession: undefined,
  dbSession: undefined,
  replaySession: undefined,
  sessionId: undefined,
});

export const getEmptyWebsockets = (): WebSockets => ({
  olServerAndClient: undefined,
  olWebsocket: undefined,
  dbServerAndClient: undefined,
  dbWebsocket: undefined,
  replayServerAndClient: undefined,
  replayWebsocket: undefined,
});

function extractLoginData(loginResponse: LoginResponse) {
  const sessionId = loginResponse['sessionUuid'];
  const email = loginResponse['email'];
  const userId = loginResponse['userUuid'];
  const groups =
    typeof loginResponse['groups'] === 'string'
      ? loginResponse['groups'].split(',').map((item) => item.trim())
      : [];
  const username = loginResponse['userName'];
  let reason = loginResponse['reason'];
  const changePassword = loginResponse['changePassword'];
  const accountDisabled = loginResponse['accountDisabled'];
  const success = loginResponse['success'];

  if (accountDisabled) {
    reason = 'ACCOUNT_DISABLED';
  } else if (changePassword) {
    reason = 'CHANGE_PASSWORD';
  } else if (
    typeof reason === 'string' &&
    reason.includes('Too many active sessions')
  ) {
    reason = 'TOO_MANY_SESSIONS';
  }

  return {
    sessionId,
    email,
    groups,
    username,
    userId,
    reason,
    success,
    changePassword,
    accountDisabled,
  };
}

export function isWebSocketError(response: any): boolean {
  return (
    typeof response === 'object' &&
    response !== null &&
    'success' in response &&
    !response.success
  );
}

// Helper to extract error message from response
function getErrorMessage(response: any): string {
  const originalMessage =
    response.errorMessage ||
    response.errorMsg ||
    response.error?.message ||
    'Unknown WebSocket error';

  // Provide user-friendly messages for specific error types
  if (originalMessage.includes('Limit of jobs exceeded')) {
    const match = originalMessage.match(
      /Limit of jobs exceeded: (\d+) of (\d+)/,
    );
    if (match) {
      const [, current, total] = match;
      return `Maximum number of reports reached (${current}/${total} reports).`;
    }
    return 'Maximum number of reports reached.';
  }

  if (
    originalMessage.includes('dbservercontroller.cpp') &&
    originalMessage.includes('internal error')
  ) {
    return 'An internal server error occurred. Please try again or contact your administrator if the problem persists.';
  }

  return originalMessage;
}

// Helper to determine error type from response
function getErrorType(response: any): string {
  // Check for error in the response.error structure (JSON-RPC errors)
  if (response.error) {
    const errorMessage = response.error.message || '';
    if (
      errorMessage.includes('Invalid session ID') ||
      errorMessage.includes('invalid session') ||
      errorMessage.includes('NewLogonDetected') ||
      errorMessage.includes('SessionTimeout')
    ) {
      return 'invalid_session_id';
    }
    if (errorMessage.includes('SessionNotFound')) {
      return 'session_not_found';
    }
    if (
      errorMessage.includes('Invalid') &&
      errorMessage.includes('Parameters')
    ) {
      return 'invalid_parameters';
    }
    // Add specific handling for database server job limit errors
    if (errorMessage.includes('Limit of jobs exceeded')) {
      return 'db_job_limit_exceeded';
    }
    if (
      errorMessage.includes('dbservercontroller.cpp') &&
      errorMessage.includes('internal error')
    ) {
      return 'db_server_internal_error';
    }
    return 'websocket_message_error';
  }

  // Check for error messages in other fields (for backward compatibility)
  const errorMessage =
    response.errorMessage || response.errorMsg || response.message || '';
  if (
    errorMessage.includes('Invalid session ID') ||
    errorMessage.includes('invalid session') ||
    errorMessage.includes('NewLogonDetected') ||
    errorMessage.includes('SessionTimeout')
  ) {
    return 'invalid_session_id';
  }
  if (errorMessage.includes('SessionNotFound')) {
    return 'session_not_found';
  }
  // Add specific handling for database server job limit errors in other fields
  if (errorMessage.includes('Limit of jobs exceeded')) {
    return 'db_job_limit_exceeded';
  }
  if (
    errorMessage.includes('dbservercontroller.cpp') &&
    errorMessage.includes('internal error')
  ) {
    return 'db_server_internal_error';
  }

  return 'websocket_response_error';
}

// Helper to capture WebSocket errors with full context
function captureWebSocketError(
  error: Error | any,
  response: any,
  context: {
    method: string;
    params: any;
    sessionId: string;
    socketId: string;
    type: SessionType;
  },
): void {
  const errorType = getErrorType(response);
  const errorMessage =
    error instanceof Error ? error.message : getErrorMessage(response);

  // Expected backend session signals (new-logon / expired / not-found session)
  // are handled by the re-auth/reconnect flow via the dispatched `sessionError`
  // event. Reporting them here as Sentry errors only creates non-actionable
  // noise (issue CB), so skip capture for them. Genuine internal errors such as
  // "user not found" (FMK) are NOT matched and remain visible.
  if (isExpectedSessionSignal(errorMessage)) {
    return;
  }

  // Note: Session error is already dispatched in onmessage handler for immediate detection
  // This is kept for logging purposes only

  Sentry.captureException(
    error instanceof Error ? error : new Error(errorMessage),
    {
      tags: {
        sessionType: context.type,
        method: context.method,
        socketId: context.socketId,
        hasSessionId: context.sessionId !== '',
        error_type: errorType,
      },
      extra: {
        response,
        params: context.params,
        errorMessage,
        sessionId: context.sessionId || 'no-session-id',
        // Include any additional error details from the response
        ...(response.error && { error: response.error }),
        ...(response.errorCode && { errorCode: response.errorCode }),
        ...(response.errorDetails && { errorDetails: response.errorDetails }),
        ...(response.stackTrace && { stackTrace: response.stackTrace }),
      },
      fingerprint: [
        'websocket-error',
        context.type,
        context.method,
        errorType,
        response.errorCode || 'no-error-code',
      ],
    },
  );
}

// Helper to build request parameters
function buildRequestParams(params: any, sessionId: string): any {
  if (sessionId === '') {
    return params;
  }

  return {
    ...params,
    sessionUuid: sessionId,
  };
}

export function createSession(
  serverAndClient: JSONRPCServerAndClient | undefined,
  webSocket: WebSocket | undefined,
  sessionId: string,
  type: SessionType,
): Session | undefined {
  if (!serverAndClient) {
    return undefined;
  }

  const socketId = uuidv4();
  if (webSocket) {
    sessionIdBySocket.set(webSocket, sessionId);
    // Replies before this point (the login) carried no sessionUuid, so they
    // say nothing about whether this session is valid.
    lastOkAtBySocket.delete(webSocket);
  }

  return {
    sessionId,
    async request(method, params) {
      // Fail loudly instead of silently sending a request with no `sessionUuid`.
      // Every authenticated call goes through this wrapper (login/loginAzureAD
      // use `serverAndClient.request` directly), so an empty baked sessionId
      // here always means the session was created before auth or during a
      // stale reconnect. Throwing prevents the backend "Missing parameter
      // 'sessionUuid'" error and surfaces the real problem to the caller.
      if (sessionId === '') {
        throw new Error(
          `Cannot send "${method}" on ${type}: session has no sessionUuid.`,
        );
      }

      requestRateMonitor.track(SESSION_TO_CATEGORY[type]);
      const finalParams = buildRequestParams(params, sessionId);
      const context = {
        method,
        params: finalParams,
        sessionId,
        socketId,
        type,
      };

      try {
        const response = await serverAndClient.request(method, finalParams);

        // Check if the response indicates an error
        if (isWebSocketError(response)) {
          const error = new Error(getErrorMessage(response));
          captureWebSocketError(error, response, context);
          throw error;
        }

        // Also check for JSON-RPC errors
        if (response && response.error) {
          const error = new Error(response.error.message || 'WebSocket error');
          captureWebSocketError(error, response, context);
          throw error;
        }

        return response;
      } catch (error) {
        if (
          error instanceof Error &&
          isTransportUnavailableSignal(error.message)
        ) {
          // Expected during an olserver drop/reconnect: the socket closed
          // mid-request, or a request was attempted on a non-OPEN socket. Keep
          // the context as a breadcrumb instead of a Sentry error event —
          // capturing every dropped request would flood the project on each
          // drop, and the reconnect/grace-period flow already owns recovery.
          Sentry.addBreadcrumb({
            category: 'session',
            level: 'info',
            message: `Request dropped (transport unavailable): ${method} on ${type}`,
          });
        } else if (
          error instanceof Error &&
          !error.message.includes('WebSocket error') &&
          !error.message.includes('Unknown WebSocket error') &&
          // Expected session signals (new-logon / expired / not-found session)
          // are handled by the re-auth flow — don't report them as errors.
          !isExpectedSessionSignal(error.message)
        ) {
          // This is likely a network error or timeout
          Sentry.captureException(error, {
            tags: {
              sessionType: type,
              method: method,
              socketId: socketId,
              hasSessionId: sessionId !== '',
              error_type: 'websocket_network_error',
            },
            extra: {
              params: finalParams,
              sessionId: sessionId || 'no-session-id',
              originalError: error.message,
              stack: error.stack,
            },
            fingerprint: [
              'websocket-network-error',
              type,
              method,
              error.name || 'Error',
            ],
          });
        }

        // A session/transport failure is not about this request — the backend
        // rejected the session or the socket was unavailable. Tag it so the
        // shared error classifier suppresses a generic "Something went wrong"
        // toast for every polling query that rides the same dead session; the
        // reconnect/grace-period flow owns the user-facing feedback instead.
        if (
          !(error instanceof SessionUnavailableError) &&
          error instanceof Error &&
          (isTransportUnavailableSignal(error.message) ||
            isExpectedSessionSignal(error.message))
        ) {
          throw new SessionUnavailableError(error.message);
        }

        throw error;
      }
    },
    type,
    socketId,
    serverAndClient,
    webSocket,
    toJSON() {
      return { socketId, type };
    },
  };
}

export async function initializeWebsockets() {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const { webSocket: olWebsocket, serverAndClient: olServerAndClient } =
    await initializeOlWebSocket(hostname, protocol);
  const { webSocket: dbWebsocket, serverAndClient: dbServerAndClient } =
    await initializeDbWebSocket(hostname, protocol);
  const { webSocket: replayWebsocket, serverAndClient: replayServerAndClient } =
    await initializeReplayWebSocket(hostname, protocol);

  return {
    olServerAndClient,
    olWebsocket,
    dbServerAndClient,
    dbWebsocket,
    replayServerAndClient,
    replayWebsocket,
  };
}

export type LoginErrorReason =
  | 'CONNECTION_ERROR'
  | 'INVALID_CREDENTIALS'
  | 'MISSING_SESSION'
  | 'MISSING_TOTP_CODE'
  | 'INVALID_TOTP_CODE'
  | 'MFA_REQUIRED'
  | 'CHANGE_PASSWORD'
  | 'ACCOUNT_DISABLED'
  | 'TOO_MANY_SESSIONS';

function getEmptyResponse(webSockets: WebSockets, reason: LoginErrorReason) {
  const olSession = createSession(
    webSockets.olServerAndClient,
    webSockets.olWebsocket,
    '',
    'olSession',
  )!;
  const dbSession = createSession(
    webSockets.olServerAndClient,
    webSockets.dbWebsocket,
    '',
    'dbSession',
  )!;
  const replaySession = createSession(
    webSockets.olServerAndClient,
    webSockets.replayWebsocket,
    '',
    'replaySession',
  )!;

  return {
    sessions: {
      olSession: olSession,
      dbSession: dbSession,
      replaySession: replaySession,
      sessionId: undefined,
    },
    user: {
      email: '',
      groups: [],
      username: '',
      userId: '',
    },
    reason: reason,
  };
}

export async function createClientWithCredentials(
  credentials: {
    username: string;
    password: string;
  },
  webSockets: WebSockets,
  totpCode?: number,
  timeZone?: string,
): Promise<{ sessions: Sessions; user: User; reason?: LoginErrorReason }> {
  if (!webSockets.olServerAndClient) {
    return getEmptyResponse(webSockets, 'CONNECTION_ERROR');
  }

  const basePayload = {
    userName: credentials.username,
    password: credentials.password,
    serverUid: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
    locale: 'en_US',
    timeZone: timeZone ? timeZone : '',
    defaultTimeZone: 'UTC',
    stealSession: true,
    sessionUuid: '',
    applicationName: 'marinepulse',
  };

  const payload = totpCode ? { ...basePayload, totpCode } : basePayload;
  const loginResponse: LoginResponse =
    await webSockets.olServerAndClient.request('login', payload);
  const { sessionId, email, groups, username, userId, reason, success } =
    extractLoginData(loginResponse);

  if (!success && reason) {
    return getEmptyResponse(webSockets, reason as LoginErrorReason);
  } else if (!success) {
    return getEmptyResponse(webSockets, 'INVALID_CREDENTIALS');
  }

  // Save credentials to localStorage for automatic re-login on refresh
  saveCredentials({
    username: credentials.username,
    password: credentials.password,
  });

  // All the sessions here use the same session id, which is maintained by the backend
  const olSession = createSession(
    webSockets.olServerAndClient,
    webSockets.olWebsocket,
    sessionId,
    'olSession',
  )!;
  const dbSession = createSession(
    webSockets.dbServerAndClient,
    webSockets.dbWebsocket,
    sessionId,
    'dbSession',
  );
  const replaySession = createSession(
    webSockets.replayServerAndClient,
    webSockets.replayWebsocket,
    sessionId,
    'replaySession',
  );

  return {
    sessions: {
      olSession,
      dbSession,
      replaySession,
      sessionId,
    },
    user: {
      email,
      groups,
      username,
      userId,
    },
  };
}

export async function createClientWithAzure(
  idToken: string,
  accessToken: string,
  webSockets: WebSockets,
  timeZone?: string,
): Promise<{ sessions: Sessions; user: User }> {
  if (!webSockets.olServerAndClient) {
    return getEmptyResponse(webSockets, 'INVALID_CREDENTIALS');
  }

  const loginResponse: LoginResponse =
    await webSockets.olServerAndClient.request('loginAzureAD', {
      idToken: idToken,
      accessToken: accessToken,
      serverUid: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
      locale: 'en_US',
      timeZone: timeZone ? timeZone : '',
      defaultTimeZone: 'UTC',
      stealSession: true,
      sessionUuid: '',
      applicationName: 'marinepulse',
    });
  const { sessionId, email, groups, username, userId, reason, success } =
    extractLoginData(loginResponse);

  // All the sessions here use the same session id, which is maintained by the backend
  if (!success) {
    return getEmptyResponse(webSockets, reason as LoginErrorReason);
  }

  // Note: We don't save Azure credentials as they use tokens that expire
  // Azure users will need to re-authenticate on page refresh

  const olSession = createSession(
    webSockets.olServerAndClient,
    webSockets.olWebsocket,
    sessionId,
    'olSession',
  )!;
  const dbSession = createSession(
    webSockets.dbServerAndClient,
    webSockets.dbWebsocket,
    sessionId,
    'dbSession',
  );
  const replaySession = createSession(
    webSockets.replayServerAndClient,
    webSockets.replayWebsocket,
    sessionId,
    'replaySession',
  );

  return {
    sessions: {
      olSession,
      dbSession,
      replaySession,
      sessionId,
    },
    user: {
      email,
      groups,
      username,
      userId,
    },
  };
}

function connectToWebSocket(
  url: string,
  allowFailure: boolean,
): Promise<WebSocket | undefined> {
  return new Promise((resolve, reject) => {
    const socket = new WebSocket(url);
    memoryDiagnostics.registerSocket(socket);
    let isResolved = false;

    // Add timeout for WebSocket connection (especially important for optional connections like replay/dbserver)
    const timeout = allowFailure ? 5000 : 15000; // 5s for optional, 15s for required
    const timeoutId = setTimeout(() => {
      if (!isResolved) {
        console.warn(`WebSocket connection timeout for ${url}`);
        isResolved = true;

        // Close the socket if still connecting
        if (
          socket.readyState === WebSocket.CONNECTING ||
          socket.readyState === WebSocket.OPEN
        ) {
          socket.close();
        }

        if (allowFailure) {
          // For optional connections (replay/dbserver), just resolve with undefined
          resolve(undefined);
        } else {
          // For required connections (olserver), reject
          dispatchCustomEvent('sessionConnectionError', {
            websocketUrl: url,
            error: 'WebSocket connection timeout',
          });
          reject(new Error(`WebSocket connection timeout for ${url}`));
        }
      }
    }, timeout);

    socket.onopen = () => {
      if (!isResolved && socket.readyState === WebSocket.OPEN) {
        isResolved = true;
        clearTimeout(timeoutId);
        resolve(socket);
      }
    };

    socket.onerror = (event) => {
      console.error('WebSocket error observed:', event);
      if (!isResolved) {
        clearTimeout(timeoutId);
        if (!allowFailure) {
          isResolved = true;
          dispatchCustomEvent('sessionConnectionError', {
            websocketUrl: socket.url,
            error: 'WebSocket connection error',
          });
          reject(new Error('Failed to open WebSocket connection.'));
        }
        // For allowFailure=true, let onclose handle it or timeout will catch it
      }
    };

    socket.onclose = (event) => {
      if (!isResolved) {
        isResolved = true;
        clearTimeout(timeoutId);

        if (!event.wasClean && !allowFailure) {
          console.error('WebSocket closed unexpectedly:', event);

          dispatchCustomEvent('sessionConnectionError', {
            websocketUrl: socket.url,
            error: 'WebSocket connection closed unexpectedly',
            code: event?.code,
            reason: event?.reason,
          });

          dispatchWebSocketCloseEvent(socket);
          reject(new Error('WebSocket was closed uncleanly.'));
        } else {
          console.warn('WebSocket closed', event);
          resolve(undefined);
        }
      }
    };
  });
}
